import { useMemo, useRef, useState } from "react"
import { useFormik } from "formik"
import { useNavigate } from "react-router-dom"
import picker from "../EventPickerDialog/EventPickerDialog.module.css"
import s from "./QuickCreateEvent.module.css"
import { initFormValues } from "@pages/makeEvent/types/types"
import { CreateEventInfo } from "@app/data/createEvent/types"
import { useCreateEvent } from "@app/data/createEvent/useCreateEvent"
import { useEventsTypes } from "@pages/makeEvent/steps-components/eventType-component/useEventsTypes"
import AddressComponent from "@pages/makeEvent/steps-components/address-component"
import { categoryOrderIndex, isCategoryVisible } from "@common/category/useCategoryProvider"
import { EventAddressDto, useAddEventAvatarToTempDirectoryMutation } from "@app/data/tribeSwaggerApi"
import { extractErrorMessage } from "@utils/extractErrorMessage.utils"
import { logger } from "@app/data/logger/logger"

// ---- Фото: до 3 штук, сжимаем перед загрузкой ----
const MAX_PHOTOS = 3
const MAX_SIDE = 1600
type PhotoItem = { buf: ArrayBuffer; preview: string }

// Уменьшаем фото до 1600 px и сохраняем как JPEG: быстрее грузится, и серия из нескольких
// событий не гоняет по сети мегабайты оригиналов
async function prepareImage(file: File): Promise<PhotoItem> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("decode")); i.src = url
    })
    const k = Math.min(1, MAX_SIDE / Math.max(img.width, img.height))
    const c = document.createElement("canvas")
    c.width = Math.max(1, Math.round(img.width * k)); c.height = Math.max(1, Math.round(img.height * k))
    const ctx = c.getContext("2d")!
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height)
    ctx.drawImage(img, 0, 0, c.width, c.height)
    const blob: Blob = await new Promise((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error("toBlob")), "image/jpeg", 0.85))
    return { buf: await blob.arrayBuffer(), preview: URL.createObjectURL(blob) }
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Эмодзи для категорий — в том же духе, что и в «Куда пойти?»
const CATEGORY_EMOJI: Record<number, string> = {
  15: "💫", 17: "🎲", 1: "⚽", 2: "🚗", 4: "🎊", 5: "📚", 7: "🧸",
  8: "🏕", 9: "🕊", 12: "✈️", 13: "🚶", 14: "🐾", 16: "🎭", 19: "✨", 20: "🎤",
}
const emojiFor = (id: number, title?: string) =>
  CATEGORY_EMOJI[id] ?? (title?.toLowerCase() === "нетворкинг" ? "🤝" : "✨")

const DURATIONS = [
  { hours: 1, label: "1 час" },
  { hours: 2, label: "2 часа" },
  { hours: 3, label: "3 часа" },
  { hours: 5, label: "Весь вечер" },
]
const TIME_PRESETS = ["12:00", "15:00", "19:00", "21:00"]

type Step = "type" | "name" | "when" | "where" | "final" | "done"
const STEPS: Step[] = ["type", "name", "when", "where", "final"]

const STEP_TITLES: Record<Step, string> = {
  type: "Что за событие?",
  name: "Как назовём?",
  when: "Когда?",
  where: "Где?",
  final: "Последний штрих",
  done: "Событие отправлено",
}

const pad = (n: number) => String(n).padStart(2, "0")
const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
// Тот же формат, что отдаёт <input type="datetime-local"> в старом мастере
const toLocalDateTime = (d: Date) => `${toDateInput(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`

const DESCRIPTION_MIN = 10
const DESCRIPTION_MAX = 400
const NAME_MAX = 100

type Props = { onClose: () => void }

export function QuickCreateEvent({ onClose }: Props) {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>("type")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [createdId, setCreatedId] = useState<number | null>(null)

  const today = useMemo(() => new Date(), [])
  const tomorrow = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d }, [])
  const [date, setDate] = useState(toDateInput(tomorrow))
  const [time, setTime] = useState("19:00")
  const [duration, setDuration] = useState(2)

  const [addressValid, setAddressValid] = useState(false)
  const [photos, setPhotos] = useState<Array<PhotoItem | null>>(Array(MAX_PHOTOS).fill(null))
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [uploadTemp] = useAddEventAvatarToTempDirectoryMutation()
  const [priceText, setPriceText] = useState("")

  // --- Контакты ведущего ---
  const [contacts, setContacts] = useState<{telegram: string, whatsapp: string, phone: string}>({
    telegram: "", whatsapp: "", phone: ""
  })

  const buildContactInfo = () => {
    const result: Array<{contact: string, contact_type: "TELEGRAM" | "WHATSAPP" | "PHONE"}> = []
    if (contacts.telegram.trim()) result.push({ contact: contacts.telegram.trim(), contact_type: "TELEGRAM" })
    if (contacts.whatsapp.trim()) result.push({ contact: contacts.whatsapp.trim(), contact_type: "WHATSAPP" })
    if (contacts.phone.trim()) result.push({ contact: contacts.phone.trim(), contact_type: "PHONE" })
    return result
  }

  // --- Серия событий ---
  const [isSeries, setIsSeries] = useState(false)
  const [seriesDates, setSeriesDates] = useState<string[]>([])
  const [publishing, setPublishing] = useState(false)
  const [result, setResult] = useState<{ created: number; total: number; failed: string[]; error: string | null } | null>(null)
  const lastError = useRef<string | null>(null)
  const [seriesProgress, setSeriesProgress] = useState({ done: 0, total: 0 })

  const addSeriesDate = (d: string) => {
    if (d && !seriesDates.includes(d)) {
      setSeriesDates(prev => [...prev, d].sort())
    }
  }
  const removeSeriesDate = (d: string) => {
    setSeriesDates(prev => prev.filter(x => x !== d))
  }
  const formatSeriesDate = (d: string) => {
    const dt = new Date(d + "T12:00")
    return dt.toLocaleDateString("ru-RU", { day: "numeric", month: "short", weekday: "short" })
  }

  const formik = useFormik<CreateEventInfo>({
    initialValues: { ...initFormValues(), is_private: false },
    onSubmit: () => {},
  })
  const v = formik.values

  const { eventTypesList, isLoading: typesLoading } = useEventsTypes()
  const types = useMemo(() => {
    return eventTypesList
      .filter(t => isCategoryVisible(t.id))
      .sort((a, b) => categoryOrderIndex(a.id, a.type_name) - categoryOrderIndex(b.id, b.type_name))
  }, [eventTypesList])
  const selectedType = types.find(t => t.id === v.event_type_id)

  // --- Валидация шагов ---
  const nameOk = v.event_name.trim().length >= 3 && v.event_name.length <= NAME_MAX
  const descLen = v.description.trim().length
  const descOk = descLen >= DESCRIPTION_MIN && descLen <= DESCRIPTION_MAX

  const start = new Date(`${date}T${time}`)
  const startOk = !isNaN(start.getTime()) && start.getTime() > Date.now()

  const price = Number(priceText.replace(",", "."))
  const priceOk = v.is_free || (priceText.trim() !== "" && price > 0)
  const photoOk = photos.some(Boolean)   // нужно хотя бы одно фото, первое — обложка

  const { build } = useCreateEvent({
    onError: (message) => { lastError.current = message },
  })

  const onPickPhoto = async (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    setPhotoError(null)
    try {
      const item = await prepareImage(file)
      setPhotos(prev => { const n = [...prev]; n[i] = item; return n })
    } catch {
      setPhotoError("Не удалось прочитать фото. Выбери файл JPG или PNG.")
    }
  }
  const removePhoto = (i: number) => setPhotos(prev => {
    if (prev[i]) URL.revokeObjectURL(prev[i]!.preview)
    const n = [...prev]; n[i] = null; return n
  })

  // Бэкенд копирует загруженное фото из временной папки и не принимает одно и то же имя файла дважды,
  // поэтому для каждого события (каждой даты серии) фото загружаем заново.
  const uploadPhotos = async (): Promise<string[]> => {
    const names: string[] = []
    for (const p of photos) {
      if (!p) continue
      const res = await uploadTemp({
        // @ts-ignore — как и в useUploadFotoToServer: массив байтов
        imageDto: { image: [...new Uint8Array(p.buf)], content_type: "image/jpeg" },
      }).unwrap()
      names.push(res.unique_file_name as string)
    }
    return names
  }

  const goNext = () => {
    const i = STEPS.indexOf(step)
    if (i < STEPS.length - 1) setStep(STEPS[i + 1])
  }
  const goBack = () => {
    const i = STEPS.indexOf(step)
    if (i > 0) setStep(STEPS[i - 1])
  }

  const pickType = (id: number) => {
    formik.setFieldValue("event_type_id", id)
    setStep("name")
  }

  const confirmWhen = () => {
    const end = new Date(start.getTime() + duration * 60 * 60 * 1000)
    formik.setFieldValue("start_time", toLocalDateTime(start))
    formik.setFieldValue("end_time", toLocalDateTime(end))
    goNext()
  }

  const onAddress = (addr: EventAddressDto | null) => setAddressValid(!!addr)

  const buildPayload = (startDate: string): CreateEventInfo => {
    const s = new Date(`${startDate}T${time}`)
    const e = new Date(s.getTime() + duration * 60 * 60 * 1000)
    const base = {
      ...v,
      event_name: v.event_name.trim(),
      description: v.description.trim(),
      start_time: toLocalDateTime(s),
      end_time: toLocalDateTime(e),
      avatars_for_adding: (v.avatars_for_adding || []).filter(Boolean),
      is_private: !!v.is_private,
      contact_info: buildContactInfo(),
    }
    const { event_price: _omit, ...withoutPrice } = base as CreateEventInfo & { event_price?: number }
    return v.is_free
      ? { ...withoutPrice, is_free: true }
      : { ...withoutPrice, is_free: false, event_price: price }
  }

  const publish = async () => {
    setSubmitError(null)
    setPublishing(true)
    const dates = isSeries && seriesDates.length > 0 ? seriesDates : [date]
    setSeriesProgress({ done: 0, total: dates.length })
    const failed: string[] = []
    let created = 0
    let firstId: number | null = null
    let firstError: string | null = null
    for (let i = 0; i < dates.length; i++) {
      setSeriesProgress({ done: i, total: dates.length })
      lastError.current = null
      try {
        const names = await uploadPhotos()
        const res = await build({ ...buildPayload(dates[i]), avatars_for_adding: names })
        if (res) {
          created++
          if (firstId === null) firstId = res.event_id ?? null
        } else {
          failed.push(dates[i])
          firstError = firstError ?? lastError.current ?? "Не удалось создать событие"
        }
      } catch (err) {
        failed.push(dates[i])
        firstError = firstError ?? extractErrorMessage(err) ?? "Не удалось загрузить фото"
      }
      if (i < dates.length - 1) await new Promise(r => setTimeout(r, 400))
    }
    setPublishing(false)
    if (created > 0) {
      logger.info(`Пользователь создал мероприятий: ${created} из ${dates.length} (${v.event_name})`)
      setCreatedId(firstId)
      setResult({ created, total: dates.length, failed, error: firstError })
      setStep("done")
    } else {
      setSubmitError(firstError ?? "Не удалось создать событие")
    }
  }

  const stepIdx = STEPS.indexOf(step)

  const whenLabel = startOk
    ? start.toLocaleDateString("ru-RU", { day: "numeric", month: "long" }) + `, ${time}`
    : null

  return (
    <div className={picker.overlay} onClick={onClose}>
      <div className={`${picker.dialog} ${s.root}`} onClick={e => e.stopPropagation()} role="dialog" aria-label="Создание события" style={{position:"relative"}}>
        {publishing && (
          <div className={picker.submitOverlay}>
            <div className={picker.submitOverlaySpinner} />
            <span className={picker.submitOverlayText}>
              {seriesProgress.total > 1
                ? `Создаём ${seriesProgress.done + 1} из ${seriesProgress.total}…`
                : "Создаём событие…"}
            </span>
          </div>
        )}
        <div className={picker.dialogHeader}>
          <div className={s.headerLeft}>
            {stepIdx > 0 && step !== "done" && (
              <button className={picker.closeBtn} onClick={goBack} aria-label="Назад">←</button>
            )}
            <div className={picker.dialogTitle}>
              {selectedType && step !== "type" && step !== "done" && (
                <span className={picker.cityBadge}>{emojiFor(selectedType.id, selectedType.type_name)} {selectedType.type_name}</span>
              )}
              {whenLabel && (step === "where" || step === "final") && (
                <span className={picker.cityBadge}>🕐 {whenLabel}</span>
              )}
            </div>
          </div>
          <button className={picker.closeBtn} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>

        <h2 className={picker.stepTitle}>{STEP_TITLES[step]}</h2>

        {/* Шаг 1 — Категория */}
        {step === "type" && (
          typesLoading ? (
            <div className={picker.loading}><div className={picker.spinner} /><p>Загружаем категории...</p></div>
          ) : (
            <div className={s.typesGrid}>
              {types.map(t => (
                <button
                  key={t.id}
                  className={`${picker.optionBtn} ${v.event_type_id === t.id ? s.optionActive : ""}`}
                  onClick={() => pickType(t.id)}
                >
                  <span className={picker.optionEmoji}>{emojiFor(t.id, t.type_name)}</span>
                  <span>{t.type_name}</span>
                </button>
              ))}
            </div>
          )
        )}

        {/* Шаг 2 — Название и описание */}
        {step === "name" && (
          <>
            <input
              className={picker.cityInput}
              placeholder="Например: Настолки в антикафе"
              value={v.event_name}
              maxLength={NAME_MAX}
              autoFocus
              onChange={e => formik.setFieldValue("event_name", e.target.value)}
            />
            <div className={s.field}>
              <textarea
                className={`${picker.cityInput} ${s.textarea}`}
                placeholder="Что будет, для кого, что взять с собой"
                value={v.description}
                maxLength={DESCRIPTION_MAX}
                onChange={e => formik.setFieldValue("description", e.target.value)}
              />
              <span className={`${s.hint} ${descLen > 0 && !descOk ? s.hintError : ""}`}>
                {descLen < DESCRIPTION_MIN
                  ? `Ещё минимум ${DESCRIPTION_MIN - descLen} симв.`
                  : `${descLen} / ${DESCRIPTION_MAX}`}
              </span>
            </div>
            <button className={picker.continueBtn} disabled={!nameOk || !descOk} onClick={goNext}>
              Дальше
            </button>
          </>
        )}

        {/* Шаг 3 — Когда */}
        {step === "when" && (
          <>
            <div className={s.chipsRow}>
              <button
                className={`${picker.tag} ${date === toDateInput(today) ? picker.tagActive : ""}`}
                onClick={() => setDate(toDateInput(today))}
              >Сегодня</button>
              <button
                className={`${picker.tag} ${date === toDateInput(tomorrow) ? picker.tagActive : ""}`}
                onClick={() => setDate(toDateInput(tomorrow))}
              >Завтра</button>
              <input
                type="date"
                className={`${picker.cityInput} ${s.inlineInput}`}
                value={date}
                min={toDateInput(today)}
                onChange={e => setDate(e.target.value)}
                aria-label="Дата"
              />
            </div>

            <p className={s.label}>Начало</p>
            <div className={s.chipsRow}>
              {TIME_PRESETS.map(t => (
                <button key={t} className={`${picker.tag} ${time === t ? picker.tagActive : ""}`} onClick={() => setTime(t)}>
                  {t}
                </button>
              ))}
              <input
                type="time"
                className={`${picker.cityInput} ${s.inlineInput}`}
                value={time}
                onChange={e => setTime(e.target.value)}
                aria-label="Время начала"
              />
            </div>

            <p className={s.label}>Сколько длится</p>
            <div className={s.chipsRow}>
              {DURATIONS.map(d => (
                <button
                  key={d.hours}
                  className={`${picker.tag} ${duration === d.hours ? picker.tagActive : ""}`}
                  onClick={() => setDuration(d.hours)}
                >{d.label}</button>
              ))}
            </div>

            {!startOk && <p className={picker.errorText}>Это время уже прошло — выбери другое.</p>}

            {/* Режим серии событий */}
            <div style={{ marginTop: "16px", borderTop: "1px solid var(--borders)", paddingTop: "12px" }}>
              <button
                className={`${picker.tag} ${isSeries ? picker.tagActive : ""}`}
                onClick={() => { setIsSeries(v => !v); setSeriesDates([]) }}
                style={{ marginBottom: "8px" }}
              >
                🔁 Серия событий
              </button>

              {isSeries && (
                <div>
                  <p className={s.hint} style={{ marginBottom: "8px" }}>
                    Добавь все даты — создадим одинаковые события на каждую из них
                  </p>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                    <input
                      type="date"
                      className={`${picker.cityInput} ${s.inlineInput}`}
                      min={toDateInput(today)}
                      onChange={e => { if (e.target.value) addSeriesDate(e.target.value); e.target.value = "" }}
                      aria-label="Добавить дату в серию"
                    />
                    <span className={s.hint}>{seriesDates.length} дат</span>
                  </div>
                  {seriesDates.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", maxHeight: "120px", overflowY: "auto" }}>
                      {seriesDates.map(d => (
                        <span key={d} style={{
                          display: "flex", alignItems: "center", gap: "4px",
                          background: "var(--btns-selected)", borderRadius: "20px",
                          padding: "4px 10px", fontSize: "13px", color: "var(--primary-text)"
                        }}>
                          {formatSeriesDate(d)}
                          <button
                            onClick={() => removeSeriesDate(d)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--secondary-text)", padding: "0 2px", fontSize: "14px" }}
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              className={picker.continueBtn}
              disabled={isSeries ? seriesDates.length === 0 : !startOk}
              onClick={isSeries ? goNext : confirmWhen}
            >
              Дальше {isSeries && seriesDates.length > 0 ? `(${seriesDates.length} событий)` : ""}
            </button>
          </>
        )}

        {/* Шаг 4 — Где */}
        {step === "where" && (
          <>
            <div className={s.addressBox}>
              <AddressComponent formik={formik} setValidAddress={onAddress} />
            </div>
            {!addressValid && <p className={s.hint}>Выбери город, затем улицу и дом из подсказок.</p>}
            <button className={picker.continueBtn} disabled={!addressValid} onClick={goNext}>
              Дальше
            </button>
          </>
        )}

        {/* Шаг 5 — Фото, цена, доступ */}
        {step === "final" && (
          <>
            <div className={s.photoBox}>
              <div className={s.photoSlots}>
                {photos.map((p, i) => (
                  <div key={i} className={`${s.photoSlot} ${p ? s.photoSlotFilled : ""}`}>
                    {p ? (
                      <>
                        <img src={p.preview} alt="" />
                        {i === 0 && <span className={s.photoCover}>Обложка</span>}
                        <button type="button" className={s.photoRemove} onClick={() => removePhoto(i)} aria-label="Убрать фото">✕</button>
                      </>
                    ) : (
                      <label className={s.photoAdd}>
                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => onPickPhoto(i, e)} />
                        <span className={s.photoPlus}>＋</span>
                        <small>{i === 0 ? "Обложка" : "Ещё фото"}</small>
                      </label>
                    )}
                  </div>
                ))}
              </div>
              <span className={s.hint}>
                {photoError ?? (photoOk ? "До 3 фото. Первое — обложка события" : "Добавь хотя бы одно фото — без него событие не опубликовать")}
              </span>
            </div>

            <p className={s.label}>Вход</p>
            <div className={s.chipsRow}>
              <button
                className={`${picker.tag} ${v.is_free ? picker.tagActive : ""}`}
                onClick={() => formik.setFieldValue("is_free", true)}
              >Бесплатно</button>
              <button
                className={`${picker.tag} ${!v.is_free ? picker.tagActive : ""}`}
                onClick={() => formik.setFieldValue("is_free", false)}
              >Платно</button>
              {!v.is_free && (
                <input
                  className={`${picker.cityInput} ${s.inlineInput}`}
                  inputMode="decimal"
                  placeholder="Цена, ₽"
                  value={priceText}
                  onChange={e => setPriceText(e.target.value.replace(/[^\d.,]/g, ""))}
                  aria-label="Цена в рублях"
                  autoFocus
                />
              )}
            </div>

            <p className={s.label}>Кто увидит</p>
            <div className={s.chipsRow}>
              <button
                className={`${picker.tag} ${!v.is_private ? picker.tagActive : ""}`}
                onClick={() => formik.setFieldValue("is_private", false)}
              >Все в ленте</button>
              <button
                className={`${picker.tag} ${v.is_private ? picker.tagActive : ""}`}
                onClick={() => formik.setFieldValue("is_private", true)}
              >Только по ссылке</button>
              <button
                className={`${picker.tag} ${v.is_eighteen_year_limit ? picker.tagActive : ""}`}
                onClick={() => formik.setFieldValue("is_eighteen_year_limit", !v.is_eighteen_year_limit)}
              >18+</button>
            </div>

            <p className={s.label}>Контакты для записи</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "18px", width: "28px", textAlign: "center" }}>✈️</span>
                <input
                  className={picker.cityInput}
                  placeholder="Telegram: @username"
                  value={contacts.telegram}
                  onChange={e => setContacts(prev => ({ ...prev, telegram: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "18px", width: "28px", textAlign: "center" }}>💬</span>
                <input
                  className={picker.cityInput}
                  placeholder="WhatsApp: +79001234567"
                  value={contacts.whatsapp}
                  onChange={e => setContacts(prev => ({ ...prev, whatsapp: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "18px", width: "28px", textAlign: "center" }}>📞</span>
                <input
                  className={picker.cityInput}
                  placeholder="Телефон: +79001234567"
                  value={contacts.phone}
                  type="tel"
                  onChange={e => setContacts(prev => ({ ...prev, phone: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            <p className={s.hint} style={{ marginBottom: "12px" }}>
              Укажи хотя бы один способ связи — через него запишутся участники
            </p>

            {submitError && <p className={picker.errorText}>⚠️ {submitError}</p>}
            <button
              className={picker.continueBtn}
              disabled={!photoOk || !priceOk || publishing}
              onClick={publish}
            >
              {publishing
                ? <><span className={picker.btnSpinner} />Создаём…</>
                : (isSeries && seriesDates.length > 1 ? `Опубликовать (${seriesDates.length} событий)` : "Опубликовать")}
            </button>
          </>
        )}

        {/* Готово */}
        {step === "done" && (
          <div className={picker.emptyState}>
            <span className={picker.emptyIcon}>🎉</span>
            <p className={s.doneTitle}>{v.event_name}</p>
            <div className={s.doneInfo}>
              {result && result.created > 1 && <p><b>Создано событий: {result.created}</b></p>}
              <p>{result && result.created > 1
                ? "Все отправлены на проверку. Каждое модератор одобряет отдельно — после одобрения они появятся в ленте и на карте."
                : "Событие отправлено на проверку. Обычно это занимает несколько часов — после одобрения оно появится в ленте и на карте."}</p>
              {result && result.failed.length > 0 && (
                <p style={{ marginTop: "8px", color: "#ff6b6b" }}>
                  ⚠️ Не создались {result.failed.length} из {result.total}: {result.failed.map(formatSeriesDate).join("; ")}
                  {result.error ? ` (${result.error})` : ""}
                </p>
              )}
              <p style={{marginTop: "8px", fontSize: "13px", opacity: 0.7}}>Пока событие на модерации, открыть его страницу нельзя.</p>
            </div>
            <div className={s.doneActions}>
              {result && result.created > 0 && (
                <button
                  className={picker.continueBtn}
                  onClick={() => { onClose(); navigate(`/profile/myevents`) }}
                >
                  Мои события
                </button>
              )}
              <button className={picker.resetBtn} onClick={onClose}>Закрыть</button>
            </div>
          </div>
        )}

        {step !== "done" && (
          <div className={picker.progress}>
            {STEPS.map((st, i) => (
              <div key={st} className={`${picker.progressDot} ${i <= stepIdx ? picker.progressDotActive : ""}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default QuickCreateEvent
